import type { BaseComponent } from '../types/protocols';
import { isRectangle, type RectangleShape } from '../types/GeometryShapes';

/**
 * Modular size ("modulmått") of a window/door, in meters.
 * Derived from the first rectangle shape in `areaShapes`.
 */
export interface ModularSizeMeters {
    width: number;
    height: number;
}

export function getModularSize(component: Pick<BaseComponent, 'areaShapes'>): ModularSizeMeters | null {
    const shapes = component.areaShapes?.value;
    if (!shapes) return null;
    const rectangle = shapes.find((shape): shape is RectangleShape => isRectangle(shape));
    if (!rectangle) return null;
    if (typeof rectangle.width !== 'number' || typeof rectangle.height !== 'number') return null;
    return { width: rectangle.width, height: rectangle.height };
}

/** Formats a modular size in decimeters, e.g. 0.9m × 1.2m → "9x12". */
export function formatModularSize(size: ModularSizeMeters): string {
    return `${Math.round(size.width * 10)}x${Math.round(size.height * 10)}`;
}

export interface ModularSizeGroup<T> {
    /** Stable key, e.g. "9x12". */
    key: string;
    /** Display title, e.g. "9x12". */
    title: string;
    widthDm: number;
    heightDm: number;
    items: T[];
}

export interface ModularSizeGrouping<T> {
    /** Groups sorted by width, then height (ascending). */
    groups: ModularSizeGroup<T>[];
    /** Items with no rectangle shape (no modulmått). */
    unsized: T[];
}

/**
 * Groups components by modular size. Sizes are compared in whole decimeters,
 * so 0.9m and 0.904m land in the same "9x..." group.
 */
export function groupByModularSize<T extends Pick<BaseComponent, 'areaShapes'>>(items: T[]): ModularSizeGrouping<T> {
    const groups = new Map<string, ModularSizeGroup<T>>();
    const unsized: T[] = [];

    items.forEach(item => {
        const size = getModularSize(item);
        if (!size) {
            unsized.push(item);
            return;
        }
        const widthDm = Math.round(size.width * 10);
        const heightDm = Math.round(size.height * 10);
        const key = `${widthDm}x${heightDm}`;
        if (!groups.has(key)) {
            groups.set(key, { key, title: key, widthDm, heightDm, items: [] });
        }
        groups.get(key)!.items.push(item);
    });

    const sorted = Array.from(groups.values()).sort((a, b) =>
        a.widthDm - b.widthDm || a.heightDm - b.heightDm
    );

    return { groups: sorted, unsized };
}
